import type { FC } from "react";

import * as _ from "lodash";

import Chart from "../../../../components/Chart/src/Chart";

import chartConfig from "./chartConfig";

export interface IScatterData {
    name?: string;
    icon?: string;
    color?: string;
    link?: string;
    data: IScatterChartPoint[];
}

export interface IScatterMetric {
    name: string;
    valueFormat?: (value: number) => string;
    highLabel: string;
    lowLabel: string;
    midValue?: number;
}

interface IScatterChartPoint {
    x: number;
    y: number;
}

interface IScatterChartProps {
    data: IScatterData[];
    config?: any;
    metrics?: {
        [dimension: string]: IScatterMetric;
    };
    options?: {
        efficiencyZones?: boolean;
    };
    translate?: any;
}

const CHART_TYPE = "scatter";
const MINMAX_PADDING_BIG_MULTIPLIER = 0.15;
const MINMAX_PADDING_SMALL_MULTIPLIER = 0.05;

function getAxisMinMax(
    { minX, minY, maxX, maxY }: { minX: number; minY: number; maxX: number; maxY: number },
    { bigMultiplier, smallMultiplier } = {
        bigMultiplier: MINMAX_PADDING_BIG_MULTIPLIER,
        smallMultiplier: MINMAX_PADDING_SMALL_MULTIPLIER,
    },
) {
    let xMultiplier;
    let yMultiplier;

    if (maxX - minX > maxY - minY) {
        xMultiplier = bigMultiplier;
        yMultiplier = smallMultiplier;
    } else {
        xMultiplier = smallMultiplier;
        yMultiplier = bigMultiplier;
    }

    const stepX = maxX * xMultiplier;
    const stepY = maxY * yMultiplier;

    const getValue = (value: number, step: number, sign: string) => {
        if (sign === "-") {
            const min = value - step;

            return min <= 0 ? 0 : min;
        }

        return value + step;
    };

    return {
        minX: getValue(minX, stepX, "-"),
        minY: getValue(minY, stepY, "-"),
        maxX: getValue(maxX, stepX, "+"),
        maxY: getValue(maxY, stepY, "+"),
    };
}

function getMinMaxFromData(data: IScatterData[]): { [x: string]: number } {
    const getFirstPoint = (axis: string): number | undefined => _.get(data, `[0].data[0].${axis}`);
    let maxX: number | undefined;
    let maxY: number | undefined;
    let minX: number | undefined = (maxX = getFirstPoint("x"));
    let minY: number | undefined = (maxY = getFirstPoint("y"));

    data.forEach((item) => {
        if (!_.isArray(item.data)) {
            return;
        }

        item.data.forEach((points) => {
            // we do extra condition to avoid extra assignment
            if (minX === undefined || minX > points.x) {
                minX = points.x;
            } else if (maxX === undefined || maxX < points.x) {
                maxX = points.x;
            }

            if (minY === undefined || minY > points.y) {
                minY = points.y;
            } else if (maxY === undefined || maxY < points.y) {
                maxY = points.y;
            }
        });
    });

    return { minX: minX ?? 0, minY: minY ?? 0, maxX: maxX ?? 0, maxY: maxY ?? 0 };
}

export const ConversionScatterChart: FC<IScatterChartProps> = ({ data, config, metrics, options, translate }) => {
    const efficiencyZones = options?.efficiencyZones ?? true;
    const { minX: oMinX, minY: oMinY, maxX: oMaxX, maxY: oMaxY } = getMinMaxFromData(data);
    const { minX, minY, maxX, maxY } = getAxisMinMax({
        minX: oMinX,
        minY: oMinY,
        maxX: oMaxX,
        maxY: oMaxY,
    });
    const midValues: Record<string, number> = {
        x: oMinX + (oMaxX - oMinX) / 2,
        y: oMinY + (oMaxY - oMinY) / 2,
    };

    if (metrics) {
        Object.keys(metrics).forEach((dimension) => {
            if (metrics[dimension].midValue) {
                midValues[dimension] = metrics[dimension].midValue!;
            }
        });
    }

    const finalConfig = chartConfig(
        {
            type: CHART_TYPE,
            config,
            metrics,
            axis: { minX, minY, maxX, maxY },
            midValues,
            efficiencyZones,
        },
        translate,
    );

    return (
        <Chart
            // force re-render when pressing the "benchmark" button
            key={finalConfig.series ? "withSeries" : "withoutSeries"}
            type={CHART_TYPE}
            data={data}
            config={finalConfig}
        />
    );
};
